import { parsePageLogic, resolveNextPageId } from "./evaluatePageLogic";
import type { FormAnswerMap, PageLogicPageRef } from "./types";

export interface PageLogicValidationIssue {
    page_id: string;
    message: string;
}

function collectInputIdsUpToPage(pages: PageLogicPageRef[], pageId: string): Set<string> {
    const sorted = [...pages].sort((a, b) => a.page_index - b.page_index);
    const targetIndex = sorted.findIndex((page) => page.page_id === pageId);
    const ids = new Set<string>();
    for (const page of sorted) {
        if (targetIndex >= 0 && page.page_index > sorted[targetIndex]!.page_index) break;
        for (const input of (page as PageLogicPageRef & { inputs?: { input_id: string }[] }).inputs ?? []) {
            ids.add(input.input_id);
        }
    }
    return ids;
}

function detectCycleFromPage(startPageId: string, pages: PageLogicPageRef[]): boolean {
    const visited = new Set<string>();
    let currentId: string | null = startPageId;
    const emptyAnswers: FormAnswerMap = {};

    for (let step = 0; step <= pages.length; step++) {
        if (!currentId) return false;
        if (visited.has(currentId)) return true;
        visited.add(currentId);

        const resolution = resolveNextPageId(currentId, pages, emptyAnswers);
        if (resolution.kind === "end") return false;
        currentId = resolution.page_id;
    }

    return true;
}

export function validatePageLogic(
    pages: (PageLogicPageRef & { inputs?: { input_id: string; input_label?: string | null }[] })[]
): PageLogicValidationIssue[] {
    const issues: PageLogicValidationIssue[] = [];
    const pageIds = new Set(pages.map((page) => page.page_id));

    for (const page of pages) {
        const logic = parsePageLogic(page.page_logic);
        if (!logic) continue;

        const allowedInputIds = collectInputIdsUpToPage(pages, page.page_id);

        for (const [index, rule] of logic.rules.entries()) {
            if (!allowedInputIds.has(rule.when.input_id)) {
                issues.push({
                    page_id: page.page_id,
                    message: `Rule ${index + 1} references a question that is not on this page or earlier.`,
                });
            }

            if (rule.then.action === "jump" && !pageIds.has(rule.then.target_page_id)) {
                issues.push({
                    page_id: page.page_id,
                    message: `Rule ${index + 1} jumps to a page that does not exist.`,
                });
            }
        }

        if (logic.fallback.action === "jump" && !pageIds.has(logic.fallback.target_page_id)) {
            issues.push({
                page_id: page.page_id,
                message: "Fallback jumps to a page that does not exist.",
            });
        }

        if (detectCycleFromPage(page.page_id, pages)) {
            issues.push({
                page_id: page.page_id,
                message: "Branching from this page can loop forever. Adjust rules or fallback.",
            });
        }
    }

    return issues;
}
